import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// project-imports
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";
import { useUserContext } from "../../context/user_context";
import ModalLogin from "../../components/form/ModalLogin";
import { PaymentSuccessModel } from "../../components/success-model/PaymentSuccessModel";
import { useProductContext } from "../../context/product_context";
import { useOrderContext } from "../../context/order_context";
import Spinner from "../../components/spinner-component/SpinnerOne";
import { formatCurrency, handleDiscountedPrice, handleMonthlyPrice } from "../../utils/course";
import PricingStencils from "../../components/Orders/PricingStencils";
import ErrorComponent from "../../components/error-message/ErrorComponent";
import {
  GoogleAnalyticsEnum,
  pageView,
  triggerFunction,
} from "../../utils/google-analytics";
import PurchaseHelpModal from "../../common/Modal/PurchaseHelpModal";
import OfferingDrawer from "../../common/Modal/OfferingDetailDrawer";
import ToolTip from "../../common/Tooltip/ToolTip";
import { checkMediaUrl, handleFormatProductName, maskPrice } from "../../utils";

// third-party
import moment from "moment/moment";
import FsLightbox from "fslightbox-react";
import TalkToSalesForm from "../../components/training-programs/TalkToSalesForm";

// Reading env variable
const VENDOR_NAME = process.env.REACT_APP_RAZOR_APP_VENDOR_NAME;
const RAZOR_THEME = process.env.REACT_APP_RAZOR_APP_THEME_COLOR;
const TYPE_DEVELOPER = process.env.REACT_APP_OFFERING_TYPE_DEVELOPER;
const TYPE_ADMIN = process.env.REACT_APP_OFFERING_TYPE_ADMIN;
const PRICING_BTN_TXT = "Know More";

// Plans data
const Pricing = () => {

  const { type, productCode } = useParams();
  const navigate = useNavigate();

  // Reading variables and methods from context
  const { products, productLoading, productsError } = useProductContext();
  const { loginData } = useUserContext();
  const { fetchOrders, createOrders, ordersError, orders, orderLoading } =
    useOrderContext();

  // Constant variables
  const salesforceDeveloper = products?.filter(
    (item) =>
      item?.category === "salesforce" && item?.subCategory === "development" && item?.type === "internship"
  );
  const salesforceAdmin = products?.filter(
    (item) =>
      item?.category === "salesforce" && item?.subCategory === "administration" && item?.type === "internship"
  );

  const salesforceDeveloperSelf = products?.filter(
    (item) =>
      item?.category === "salesforce" && item?.subCategory === "development" && item?.type === "flex-learning-path"
  );
  const salesforceAdminSelf = products?.filter(
    (item) =>
      item?.category === "salesforce" && item?.subCategory === "administration" && item?.type === "flex-learning-path"
  );

  const currentMember = (loginData && loginData?.data) || null;
  const metaData = (data) => data && data?.metadata && (data?.metadata || {});
  const currentActivePlan = type ? (type === TYPE_ADMIN ? salesforceAdmin : salesforceDeveloper) : salesforceAdmin;
  const currentActiveSelfLearningPlan = type ? (type === TYPE_ADMIN ? salesforceAdminSelf : salesforceDeveloperSelf) : salesforceAdminSelf;
  const metaDataTags = (data) =>
    data &&
      data?.metadata &&
      data?.metadata?.tags &&
      data?.metadata?.tags.length
      ? data?.metadata?.tags[0]
      : [];

  // Local State variable initialization
  const [state, setState] = useState({
    active: (type || TYPE_ADMIN),
    pricingPlan: currentActivePlan,
    pricingPlanSelf: currentActiveSelfLearningPlan
  });
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [paymentSucess, setPaymentSucess] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [newOrder, setNewOrder] = useState({});
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [toggler, setToggler] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("#");
  const [showTalkToSalesModal, setShowTalkToSalesModal] = useState(false);
  const [talkToSalesData, setTalkToSalesData] = useState({});

  // Constant function
  const handlePricing = (plan) => {
    try {
      if (plan === TYPE_ADMIN) {
        setState({
          ...state,
          active: TYPE_ADMIN,
          pricingPlan: salesforceAdmin,
          pricingPlanSelf: salesforceAdminSelf
        });
        navigate(`/our-offerings/${process.env.REACT_APP_OFFERING_TYPE_ADMIN}`);
      } else {
        setState({
          ...state,
          active: TYPE_DEVELOPER,
          pricingPlan: salesforceDeveloper,
          pricingPlanSelf: salesforceDeveloperSelf
        });
        navigate(`/our-offerings/${process.env.REACT_APP_OFFERING_TYPE_DEVELOPER}`);
      }
    } catch (error) {
      console.log(
        "66 : Pricing : Error in handlePricing function : ",
        error && JSON.stringify(error)
      );
    }
  };

  const handleInfoBtnClick = async (planData) => {
    try {
      if (!currentMember || !currentMember?.userId) {
        setPendingPlan(planData);
        setNotLoggedIn(true);
        return;
      }
      triggerFunction(
        window.location.pathname + " " + GoogleAnalyticsEnum.BUTTON,
        GoogleAnalyticsEnum.SALESFORCE_PRICING_PLAN_CHOOSE_BUTTON
      );

      const userUniqueId = currentMember && ((currentMember?.firebaseUid ? currentMember?.firebaseUid : currentMember?.userRecordId));
      await fetchOrders(userUniqueId);

      if (planData) {
        const orderData = { ...planData, ...currentMember };
        // Delete unnecessary details from the plan
        const orderPayload = {
          amount: isDiscountValid(orderData)
            ? handleDiscountedPrice(planData, false)?.finalAmount * 100
            : orderData?.pricing?.priceInSmallUnit,
          amountUnit: "paise",
          discount: orderData?.discount?.discountPercent || 0.0,
          orderCurrency: orderData?.pricing?.priceCurrency || "INR",
          receipt: `lsa_portal_order_${Date.now()}`,
          amountPaid: 0.0,
          createdAt: moment(new Date()).format("YYYY-MM-DD"),
          vendorName: VENDOR_NAME,
          customer: {
            customerId: orderData?.firebaseUid || orderData?.userRecordId,
            email: orderData?.userId,
            fullName: orderData?.userFullName,
          },
          notes: [
            {
              id: orderData?.id,
              productCode: orderData?.productCode,
              name: orderData?.name,
              type: orderData?.type,
              duration: orderData?.pricing?.billingCycle,
              isActive: orderData?.isActive,
              category: orderData?.category,
              subCategory: orderData?.subCategory,
              description: orderData?.description,
              price: orderData?.pricing?.priceInSmallUnit,
              discountPercent: orderData?.discount?.discountPercent || 0.0,
              features: orderData?.features,
              thumbnailUrl: orderData?.media?.thumbnailUrl || "#",
            },
          ],
        };

        // calling create order api
        const response = await createOrders(orderPayload);

        // Addded this
        if (!ordersError && response !== "error") {
          const options = {
            key: process.env.REACT_APP_RAZOR_APP_KEY,
            order_id: response?.razorPayOrderId,
            amount: isDiscountValid(orderData)
              ? handleDiscountedPrice(planData, false)?.finalAmount * 100
              : orderData?.pricing?.priceInSmallUnit,
            currency: orderData?.currency,
            name: VENDOR_NAME,
            description: orderData?.description,
            image: process.env.REACT_APP_LOGO_URL,
            handler: function (response) {
              // alert("Payment Successful! Payment ID: " + response.razorpay_payment_id);
              setNewOrder({
                ...orderPayload,
                paymentId: response.razorpay_payment_id,
              });
              setPaymentSucess(true);
              // window.location.href = `/api/download-pdf?paymentId=${response.razorpay_payment_id}`;
              // downloadEbook({paymentId: response?.razorpay_payment_id, productId:orderData?.productCode });
            },
            prefill: {
              name: orderData?.userFullName,
              email: orderData?.userId,
              contact: "",
            },
            theme: {
              color: RAZOR_THEME,
            },
            notify: {
              sms: true,
              email: true,
            },
            notes: {
              item_description: orderData?.description, // You can add item details here as well
            },
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      }
    } catch (error) {
      console.log(
        "157 : Pricing : Error in handleInfoBtnClick function : ",
        error && JSON.stringify(error)
      );
    }
  };

  const isDiscountValid = (data) => {
    try {
      if (
        data?.discount?.hasDiscount &&
        handleDiscountedPrice(data, false)?.isValid
      ) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(
        "173 : Pricing : Error in isDiscountValid : ",
        error && JSON.stringify(error)
      );
    }
  };

  const isBtnDisabled = (planData) => {
    try {
      const isProductPurchased = (orders || [])?.filter(
        (order) =>
          (order?.status === "paid" || order?.status === "authorized") &&
          order?.notes &&
          order?.notes.length &&
          order?.notes[0]?.isActive &&
          order?.notes[0]?.productCode === planData?.productCode &&
          order?.customer &&
          order?.customerId === currentMember?.userRecordId
      );
      return isProductPurchased && isProductPurchased?.length ? true : false;
    } catch (error) {
      console.log(
        "169 : Pricing : Error in isBtnDisabled function : ",
        error && JSON.stringify(error)
      );
    }
  };

  const closeModalLogin = () => setNotLoggedIn(false);
  const closeModalPaymentSuccess = () => setPaymentSucess(false);

  const handleOpenHelpModal = () => {
    setOpenHelpModal(true);
  };
  const closeHelpModal = () => setOpenHelpModal(false);

  const handleOpenDetailDrawer = (data) => {
    setOpenDetailDrawer(true);
    navigate(`/our-offerings/${type}/${data?.productCode?.trim()?.toLowerCase()}`);
  };

  const handleCloseDetailDrawer = () => {
    setOpenDetailDrawer(false);
    navigate(`/our-offerings/${type}`);
  };

  const handleWatchPreview = (data) => {
    try {
      const previewUrl = data && data?.media && data?.media?.previewUrl ? data?.media?.previewUrl : ""
      if (previewUrl) {
        setPreviewUrl(previewUrl);
        setToggler(prev => !prev);
      }
    } catch (error) {
      console.log(
        "279 : Pricing : Error in handleWatchPreview method : ",
        error && JSON.stringify(error)
      );
    }
  }

  const isPaymentDone = () => {
    if (
      newOrder &&
      Object.keys(newOrder).length &&
      newOrder.paymentId &&
      paymentSucess
    ) {
      return true;
    }
  };

  const toggleTalkToSalesModal = (data) => {
    try {
      setTalkToSalesData(data);
      setShowTalkToSalesModal(!showTalkToSalesModal);
    } catch (error) {
      console.error(
        "114 : TestimonialForm : Error occurred in toggleTalkToSalesModal:",
        error && JSON.stringify(error)
      );
    }
  };

  useEffect(() => {
    if (
      window.location.pathname.includes("/our-offerings") &&
      currentMember &&
      currentMember?.userId &&
      pendingPlan
    ) {
      handleInfoBtnClick(pendingPlan);
      setPendingPlan(null);
      setNotLoggedIn(false);
    }
  }, [currentMember]);

  useEffect(() => {
    setState({
      ...state,
      pricingPlan:
        state?.active === TYPE_ADMIN
          ? salesforceAdmin
          : salesforceDeveloper,
      pricingPlanSelf:
        state?.active === TYPE_ADMIN
          ? salesforceAdminSelf
          : salesforceDeveloperSelf,
    });
  }, [products]);

  return (
    <>
      <SEO title="Pricing | Longswitch Academy" />
      {pageView(window.location.pathname, GoogleAnalyticsEnum.SF_ALL_PRICING)}
      {(productLoading || orderLoading) && <Spinner />}
      <Layout>
        <BreadcrumbOne
          title="Pricing"
          rootUrl="/"
          parentUrl="Home"
          googleEnum={GoogleAnalyticsEnum.SF_ALL_COURSES_TO_SF_COURSE_HOME}
          currentUrl="Pricing"
        />
        <div className="edu-pricing-area edu-section-gap bg-image mb--20">
          <div className="container edu-section-pricing-gapBottom">
            <div className="row g-5">
              <div className="col-lg-6">
                <div className="section-title text-start">
                  <span className="pre-title">Salesforce Career Launchpad</span>
                  <h3 className="title">From Beginner to Job-Ready</h3>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="pricing-billing-duration">
                  <ul>
                    <li className="nav-item">
                      <button
                        className={
                          state.active === TYPE_ADMIN
                            ? "nav-link active"
                            : "nav-link"
                        }
                        type="button"
                        aria-label={TYPE_ADMIN}
                        onClick={() => handlePricing(TYPE_ADMIN)}
                      >
                        Salesforce Admin
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={
                          state.active === TYPE_DEVELOPER
                            ? "nav-link active"
                            : "nav-link"
                        }
                        type="button"
                        aria-label={TYPE_DEVELOPER}
                        onClick={() => handlePricing(TYPE_DEVELOPER)}
                      >
                        Salesforce Developer
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="row y-gap-30 pt--50 justify-content-center">
              {(state.active === TYPE_DEVELOPER ||
                state.active === TYPE_ADMIN) &&
                (productLoading && !productsError ? (
                  [1, 2, 3].map((_, i) => (
                    <div className="col-lg-4 col-md-6 col-12 pricing" key={i}>
                      <PricingStencils />
                    </div>
                  ))
                ) : !productLoading && productsError ? (
                  <ErrorComponent
                    errorCode="404"
                    mainError="Failed to get the plans right now."
                    subText="Please return to the site's homepage, It looks like nothing was found at this location"
                    buttonText="Back to Home"
                    link="/"
                  />
                ) : !productLoading &&
                  !productsError &&
                  (!state?.pricingPlan ||
                    (state?.pricingPlan && !state?.pricingPlan.length)) ? (
                  <div className="col-lg-4 col-md-6 col-12">
                    <ErrorComponent
                      errorCode="500"
                      mainError="No Plans are available!"
                      subText="Currently we don't have plans. Please be back later."
                      buttonText="Back to Home"
                      link="/"
                    />
                  </div>
                ) : (
                  state.pricingPlan?.map((data, index) => (
                    <div
                      className="col-md-6 col-12 pricing"
                      key={index}
                    >
                      {isDiscountValid(data) ? (
                        <div className="wrap">
                          <span className="ribbon6 fs-5 fw-bold">
                            {/* {data?.discount?.discountLabel || ""} */}
                            Discount Available
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                      <div className={`pricing-table border border-1`}>
                        <div className="pricing-header mb-3">
                          {metaData(data)?.downloads > 0 && (
                            <div className="top-position left-top">
                              <div className="eduvibe-status status-04 text-capitalize">
                                {metaData(data)?.downloads + " Enrolled"}
                              </div>
                            </div>
                          )}
                          {data.active === true && (
                            <div className="edu-badge">
                              <span>{metaDataTags(data)}</span>
                            </div>
                          )}
                          <ToolTip title={"View Deails"} id={"pricingDetail"} placement="bottom">
                            <h3
                              className="title"
                              onClick={() => handleOpenDetailDrawer(data)}
                            >
                              {handleFormatProductName(data?.name)}
                            </h3>
                          </ToolTip>
                          <h3
                            className="title"
                            onClick={() => handleOpenDetailDrawer(data)}
                          >
                            ({data?.pricing?.billingCycle})
                          </h3>
                          <div className="price-wrap">
                            <div className="salesforceAdmin-pricing">
                              {isDiscountValid(data) && (
                                <span className="fs-4 fw-bold text-decoration-line-through">
                                  {/* {data?.pricing?.priceFormatted} */}
                                  {maskPrice(handleMonthlyPrice(data).formattedAmount)}
                                </span>
                              )}
                              <span className="amount fs-1">
                                {isDiscountValid(data)
                                  ? maskPrice(handleDiscountedPrice(data, true)?.formattedAmount)
                                  : maskPrice(handleMonthlyPrice(data).formattedAmount)}
                              </span>
                              <span className="fs-3 duration mx-1">
                                /{data?.pricing?.priceType}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* <div className="mt-3 mb-3 text-center">
                          <button
                            className="edu-btn btn-primary btn-border btn-small mx-1 fs-5"
                            onClick={() => handleWatchPreview(data)}
                          >
                            Watch Preview
                          </button>
                        </div> */}


                        {/* {data.features && data.features.length > 0 && (
                          <div className="pricing-body fw-bolder border-0 border-bottom">
                            {(data?.features[data.features.length - 1] || "")}
                          </div>
                        )} */}
                        <div className="pricing-body fw-bolder border-0 border-bottom" />

                        {data.features && data.features.length > 0 && (
                          <div className="pricing-body">
                            <ul className="list-item">
                              {(data?.features || [])?.slice(0, 10).map((feature, i) => (
                                <li key={i} className="fs-15">
                                  <i className="icon-checkbox-circle-line"></i>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="pricing-btn">
                          {isBtnDisabled(data) ? (
                            <div className="pricing-card-bottom gap-1">
                              <div className="">
                                <button
                                  className={`edu-btn btn-medium text-start fs-5 ${data.active !== true ? " btn-dark" : ""
                                    }${isBtnDisabled(data) ? " disabled" : " "}`}
                                  onClick={handleOpenHelpModal}
                                >
                                  {data.purchase?.buttonText}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                              <div className="rating eduvibe-course-rating-stars">
                                <button
                                  className={`edu-btn btn-secondary btn-border btn-medium text-start fs-5${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  onClick={() => handleOpenDetailDrawer(data)}
                                >
                                  {PRICING_BTN_TXT}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="pricing-card-bottom gap-1">
                              <div>
                                <button
                                  className={`edu-btn btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  // onClick={() => handleInfoBtnClick(data)}
                                  onClick={() => toggleTalkToSalesModal(data)}
                                >
                                  {/* {data.purchase?.buttonText} */}
                                  Talk to Sales
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                              <div className="rating eduvibe-course-rating-stars">
                                <button
                                  className={`edu-btn btn-secondary btn-border btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  onClick={() => handleOpenDetailDrawer(data)}
                                >
                                  {PRICING_BTN_TXT}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>

                            </div>
                          )}
                        </div>


                      </div>
                    </div>
                  ))
                ))}
            </div>

            <div className="row g-5 mt--20 justify-content-center">
              {(state.active === TYPE_DEVELOPER ||
                state.active === TYPE_ADMIN) &&
                (productLoading && !productsError ? (
                  [1, 2, 3].map((_, i) => (
                    <div className="col-lg-4 col-md-6 col-12 pricing" key={i}>
                      <PricingStencils />
                    </div>
                  ))
                ) : !productLoading && productsError ? (
                  <ErrorComponent
                    errorCode="404"
                    mainError="Failed to get the plans right now."
                    subText="Please return to the site's homepage, It looks like nothing was found at this location"
                    buttonText="Back to Home"
                    link="/"
                  />
                ) : !productLoading &&
                  !productsError &&
                  (!state?.pricingPlanSelf ||
                    (state?.pricingPlanSelf && !state?.pricingPlanSelf.length)) ? (
                  <div className="col-lg-4 col-md-6 col-12">
                    <ErrorComponent
                      errorCode="500"
                      mainError="No Plans are available!"
                      subText="Currently we don't have plans. Please be back later."
                      buttonText="Back to Home"
                      link="/"
                    />
                  </div>
                ) : (
                  state.pricingPlanSelf?.map((data, index) => (
                    <div
                      className="col-md-6 col-12 pricing"
                      key={index}
                    >
                      {isDiscountValid(data) ? (
                        <div className="wrap">
                          <span className="ribbon6 fs-5 fw-bold">
                            {/* {data?.discount?.discountLabel || ""} */}
                            Discount Available
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                      <div className={`pricing-table border border-1`}>
                        <div className="pricing-header mb-3">
                          {metaData(data)?.downloads > 0 && (
                            <div className="top-position left-top">
                              <div className="eduvibe-status status-04 text-capitalize">
                                {metaData(data)?.downloads + " Enrolled"}
                              </div>
                            </div>
                          )}
                          {data.active === true && (
                            <div className="edu-badge">
                              <span>{metaDataTags(data)}</span>
                            </div>
                          )}
                          <ToolTip title={"View Deails"} id={"pricingDetail"} placement="bottom">
                            <h3
                              className="title"
                              onClick={() => handleOpenDetailDrawer(data)}
                            >
                              {handleFormatProductName(data?.name)}
                            </h3>
                          </ToolTip>
                          <h3
                            className="title"
                            onClick={() => handleOpenDetailDrawer(data)}
                          >
                            ({data?.pricing?.billingCycle})
                          </h3>
                          <div className="price-wrap">
                            <div className="salesforceAdmin-pricing">
                              {/* {isDiscountValid(data) && (
                                <span className="fs-4 fw-bold text-decoration-line-through">
                                  {maskPrice(handleMonthlyPrice(data).formattedAmount)}
                                </span>
                              )} */}
                              <span className="amount fs-1">
                                {isDiscountValid(data)
                                  ? maskPrice(handleDiscountedPrice(data, true)?.formattedAmount)
                                  : maskPrice(handleMonthlyPrice(data).formattedAmount)}
                              </span>
                              <span className="fs-3 duration mx-1">

                              </span>
                            </div>
                          </div>
                        </div>


                        {/* {data.features && data.features.length > 0 && (
                          <div className="pricing-body fw-bolder border-0 border-bottom">
                            {(data?.features[data.features.length - 1] || "")}
                          </div>
                        )} */}
                        <div className="pricing-body fw-bolder border-0 border-bottom" />

                        {data.features && data.features.length > 0 && (
                          <div className="pricing-body">
                            <ul className="list-item">
                              {(data?.features || [])?.slice(0, 10).map((feature, i) => (
                                <li key={i} className="fs-15">
                                  <i className="icon-checkbox-circle-line"></i>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="pricing-btn">
                          {isBtnDisabled(data) ? (
                            <div className="pricing-card-bottom gap-1">
                              <div className="">
                                <button
                                  className={`edu-btn btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }${isBtnDisabled(data) ? " disabled" : " "}`}
                                  onClick={handleOpenHelpModal}
                                >
                                  {data.purchase?.buttonText}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                              <div className="rating eduvibe-course-rating-stars">
                                <button
                                  className={`edu-btn btn-secondary btn-border btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  onClick={() => handleOpenDetailDrawer(data)}
                                >
                                  {PRICING_BTN_TXT}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="pricing-card-bottom gap-1">
                              <div>
                                <button
                                  className={`edu-btn btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  // onClick={() => handleInfoBtnClick(data)}
                                  onClick={() => toggleTalkToSalesModal(data)}
                                >
                                  {/* {data.purchase?.buttonText} */}
                                  Talk to Sales
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>
                              <div className="rating eduvibe-course-rating-stars">
                                <button
                                  className={`edu-btn btn-secondary btn-border btn-medium text-start fs-4 ${data.active !== true ? " btn-dark" : ""
                                    }`}
                                  onClick={() => handleOpenDetailDrawer(data)}
                                >
                                  {PRICING_BTN_TXT}
                                  <i className="icon-arrow-right-line-right"></i>
                                </button>
                              </div>

                            </div>
                          )}
                        </div>


                      </div>
                    </div>
                  ))
                ))}
            </div>
          </div>
        </div>
      </Layout>
      <ModalLogin
        show={notLoggedIn}
        onCloseModalLogin={closeModalLogin}
        title={"Login to proceed payment"}
      />
      {isPaymentDone() && (
        <PaymentSuccessModel
          show={paymentSucess}
          onCloseModal={closeModalPaymentSuccess}
          response={newOrder}
        />
      )}
      <PurchaseHelpModal show={openHelpModal} onCloseModal={closeHelpModal} />
      <OfferingDrawer
        show={openDetailDrawer}
        onClose={handleCloseDetailDrawer}
        offeringsData={[...state?.pricingPlan, ...state?.pricingPlanSelf]}
        checkDiscountValidity={isDiscountValid}
        infoBtnClick={handleInfoBtnClick}
        openHelpModal={handleOpenHelpModal}
        checkBtnDisabled={isBtnDisabled}
        isLoading={orderLoading || productLoading}
        talkToSalesBtnClick={toggleTalkToSalesModal}
      />
      {showTalkToSalesModal && (
        <TalkToSalesForm
          show={showTalkToSalesModal}
          data={talkToSalesData}
          onCloseModal={toggleTalkToSalesModal}
          title={"Submit Review"}
          formStyle="rnt-contact-form rwt-dynamic-form row"
        />
      )}
      <FsLightbox toggler={toggler} sources={[previewUrl]} />
    </>
  );
};

export default Pricing;
